import { useSyncExternalStore } from 'react';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { supabase } from './supabase';
import { fetchActiveChannels, type ChannelRow } from './channels';
import {
  mapDefinitionRow,
  sortDefinitions,
  type SubscriberFieldDefinitionRow,
} from './subscriberCustomFields';
import type { SubscriberRow } from '../components/Subscribers/types';

/**
 * Session store for the subscribers list. It lives outside the route, same
 * idea as a Redux slice, so leaving the page and coming back keeps the rows
 * on screen. Realtime patches keep the list current while you are elsewhere.
 */
export type SubscribersListSnapshot = {
  rows: SubscriberRow[];
  fieldDefinitions: SubscriberFieldDefinitionRow[];
  channels: ChannelRow[];
  hydrated: boolean;
  refreshing: boolean;
  error: string | null;
};

const EMPTY: SubscribersListSnapshot = {
  rows: [],
  fieldDefinitions: [],
  channels: [],
  hydrated: false,
  refreshing: false,
  error: null,
};

let snapshot: SubscribersListSnapshot = EMPTY;
const listeners = new Set<() => void>();
let inflight: Promise<void> | null = null;
let refreshAgain = false;
let announceErrors = false;
let channel: RealtimeChannel | null = null;
let liveRefreshTimer: number | null = null;
let listScrollY = 0;

function emit() {
  for (const listener of listeners) listener();
}

function setSnapshot(patch: Partial<SubscribersListSnapshot>) {
  snapshot = { ...snapshot, ...patch };
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

export function useSubscribersList(): SubscribersListSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function rememberSubscribersListScroll(y: number) {
  listScrollY = y;
}

export function readSubscribersListScroll() {
  return listScrollY;
}

async function fetchSubscriberRows(): Promise<SubscriberRow[]> {
  const ordered = await supabase
    .from('subscribers')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(1000);

  if (!ordered.error) {
    return (ordered.data ?? []) as SubscriberRow[];
  }

  const fallback = await supabase.from('subscribers').select('*').limit(1000);
  if (fallback.error) throw fallback.error;
  return (fallback.data ?? []) as SubscriberRow[];
}

async function fetchFieldDefinitions(): Promise<SubscriberFieldDefinitionRow[]> {
  const { data, error } = await supabase
    .from('subscriber_field_definitions')
    .select('*')
    .eq('active', true)
    .order('key', { ascending: true });
  if (error) throw error;
  return sortDefinitions((data ?? []).map((raw) => mapDefinitionRow(raw as Record<string, unknown>)));
}

function rowId(row: { id?: unknown } | null | undefined): string | null {
  if (row?.id == null || row.id === '') return null;
  return String(row.id);
}

function applyRealtime(payload: RealtimePostgresChangesPayload<SubscriberRow>) {
  if (!snapshot.hydrated) {
    scheduleLiveRefresh();
    return;
  }

  if (payload.eventType === 'DELETE') {
    const id = rowId(payload.old);
    if (!id) {
      scheduleLiveRefresh();
      return;
    }
    setSnapshot({ rows: snapshot.rows.filter((row) => rowId(row) !== id) });
    return;
  }

  const next = payload.new;
  const id = rowId(next);
  if (!id) {
    scheduleLiveRefresh();
    return;
  }

  const index = snapshot.rows.findIndex((row) => rowId(row) === id);
  if (payload.eventType === 'INSERT' && index < 0) {
    setSnapshot({ rows: [next, ...snapshot.rows].slice(0, 1000) });
    return;
  }
  if (index >= 0) {
    const rows = snapshot.rows.slice();
    rows[index] = { ...rows[index], ...next };
    setSnapshot({ rows });
    return;
  }

  scheduleLiveRefresh();
}

function scheduleLiveRefresh() {
  if (liveRefreshTimer != null) window.clearTimeout(liveRefreshTimer);
  liveRefreshTimer = window.setTimeout(() => {
    liveRefreshTimer = null;
    void refreshSubscribersList({ silent: true });
  }, 400);
}

/** Keep a realtime subscription for the rest of the session, including after the page unmounts. */
export function ensureSubscribersListLive() {
  if (channel) return;
  channel = supabase
    .channel('subscribers-list-live')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'subscribers' },
      (payload) => applyRealtime(payload as RealtimePostgresChangesPayload<SubscriberRow>),
    )
    .subscribe();
}

async function doRefresh(silent: boolean) {
  setSnapshot({ refreshing: true, error: silent ? snapshot.error : null });
  try {
    const [rows, fieldDefinitions, channels] = await Promise.all([
      fetchSubscriberRows(),
      fetchFieldDefinitions().catch(() => null),
      fetchActiveChannels().catch(() => null),
    ]);
    setSnapshot({
      rows,
      fieldDefinitions: fieldDefinitions ?? snapshot.fieldDefinitions,
      channels: channels ?? snapshot.channels,
      hydrated: true,
      refreshing: false,
      error: null,
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : 'Failed to load subscribers';
    if (!silent) toast.error(msg);
    setSnapshot({
      refreshing: false,
      error: msg,
      rows: snapshot.hydrated ? snapshot.rows : [],
    });
  }
}

/** Load or refresh the list. A silent refresh keeps the current rows on screen. */
export function refreshSubscribersList(opts?: { silent?: boolean }): Promise<void> {
  if (!opts?.silent) announceErrors = true;
  if (inflight) {
    refreshAgain = true;
    return inflight;
  }

  const run = async () => {
    let passes = 0;
    do {
      refreshAgain = false;
      passes += 1;
      const silent = !announceErrors && snapshot.hydrated;
      announceErrors = false;
      await doRefresh(silent);
    } while (refreshAgain && passes < 3);
  };

  inflight = run().finally(() => {
    inflight = null;
  });
  return inflight;
}
